type RateSource = 'pequity' | 'lyric' | 'manual';

export type ExchangeRateMode = 'provider' | 'manual';

export type RateSnapshot = {
  currency_code: string;
  rate_to_usd: string;
  manual_rate_to_usd: string | null;
  source: RateSource;
  viewable: boolean;
  effective_at: string;
  snapshot_at: string;
  is_manual: boolean;
  is_default_currency: boolean;
};

export type ExchangeRateSettings = {
  rate_source: RateSource | null;
  connected_provider: 'pequity' | 'lyric';
  is_fallback_rates: boolean;
  last_synced_at: string | null;
  budgeting_currency_code: string;
  currencies_needing_rates: string[];
  snapshots: RateSnapshot[];
};

export type ExchangeRateRow = {
  currency_code: string;
  is_default_currency: boolean;
  has_rate: boolean;
  rate_to_usd: string | null;
  manual_rate_to_usd: string | null;
  viewable: boolean;
  snapshot_at: string | null;
};

export type ExchangeRateSettingsView = {
  rows: ExchangeRateRow[];
  budgetingCurrencyCode: string;
  isFallbackRates: boolean;
  lastSyncedAt: string | null;
  rateSource: RateSource | null;
  connectedProvider: 'pequity' | 'lyric';
};

// Rates are stored/sent as fixed-precision decimal strings (no thousands
// separators) so the backend DecimalField parses them cleanly.
export const RATE_PRECISION = 6;

export const formatRate = (rate: string | number | null): string => Number(rate ?? 0).toFixed(RATE_PRECISION);

const DEFAULT_RATE = formatRate(1);

export const toExchangeRateSettingsView = (data: ExchangeRateSettings): ExchangeRateSettingsView => {
  const snapshotsByCode = Object.fromEntries(data.snapshots.map((snapshot) => [snapshot.currency_code, snapshot]));
  const codes = [...new Set([...data.currencies_needing_rates, ...data.snapshots.map((s) => s.currency_code)])].sort();
  const rows = codes.map((code) => {
    const snapshot = snapshotsByCode[code];
    const isDefault = code === data.budgeting_currency_code;
    return {
      currency_code: code,
      is_default_currency: isDefault,
      has_rate: isDefault || Boolean(snapshot),
      rate_to_usd: isDefault ? DEFAULT_RATE : (snapshot?.rate_to_usd ?? null),
      manual_rate_to_usd: snapshot?.manual_rate_to_usd ?? null,
      viewable: snapshot?.viewable ?? false,
      snapshot_at: snapshot?.snapshot_at ?? null,
    };
  });
  return {
    rows,
    budgetingCurrencyCode: data.budgeting_currency_code,
    isFallbackRates: data.is_fallback_rates,
    lastSyncedAt: data.last_synced_at,
    rateSource: data.rate_source,
    connectedProvider: data.connected_provider,
  };
};

type RateChangeLogUser = {
  name: string;
  email: string;
};

// Mirrors backend RateChangeType: 0 REGENERATE, 1 MANUAL_SAVE, 2 VIEWABLE_TOGGLE
export type RateChangeType = 0 | 1 | 2;

export type RateChangeLog = {
  created_at: string;
  changed_by: RateChangeLogUser | null;
  change_type: RateChangeType;
  currency_code: string | null;
  source: string | null;
  details: { currency_codes?: string[]; rates?: Record<string, string>; viewable?: boolean } | null;
};
